import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Flex, Box } from 'reflexbox';
import {
  Panel,
  PanelHeader,
  Text,
} from 'rebass';

import { schoolColors } from 'shared/spells';
import { tokenize } from 'shared/util';

import {
  Responsive,
  WowIcon,
} from 'frontend/components';

import config from 'frontend/config';

const color = buff => {
  const c = schoolColors[buff.spell_school] || config.colors.white;
  if (_.isArray(c)) {
    return _.first(c);
  }
  return c;
};

const BuffRow = ({ buff }) => {
  const barMaxWidth = 350;
  const rowPadding = 2;
  const iconSize = 22;

  let buffName = buff.spell_name;
  const spellNameToken = tokenize(buff.spell_name);
  const simcNameToken = tokenize(buff.name);

  if (spellNameToken !== simcNameToken) {
    const leftover = _.replace(simcNameToken, spellNameToken, '');
    const parenthetical = _.startCase(_.trim(leftover, '_'));
    buffName += ` (${parenthetical})`;
  }

  return (
    <tr>
      <td style={{ padding: rowPadding }} align="center">
        <Box
          mr={1}
          style={{
            width: iconSize,
            height: iconSize,
          }}
        >
          <WowIcon
            id={buff.spell}
            type="spell"
            size={iconSize}
            extra={{
              ilvl: buff.item?.ilevel,
            }}
            border="1px solid #ffffff33"
          />
        </Box>
      </td>
      <td style={{ padding: rowPadding }}>
        <Text size={14} color={color(buff)}>
          {buffName}
        </Text>
      </td>
      <td style={{ padding: rowPadding }}>
        <Text size={14} align="right">
          {_.round(buff.uptime, 1).toFixed(1)}%
        </Text>
      </td>
      <td style={{ padding: rowPadding }}>
        <Responsive size="large">
          <Box
            style={{
              width: _.round(barMaxWidth * (buff.uptime / 100)),
              height: 10,
              backgroundColor: color(buff),
              marginRight: 'auto',
            }}
          >
            &nbsp;
          </Box>
        </Responsive>
      </td>
      <td style={{ padding: rowPadding }}>
        <Text size={14} align="right">
          {_.round((buff.start_count || 0) + (buff.refresh_count || 0))}
        </Text>
      </td>
    </tr>
  );
};

BuffRow.propTypes = {
  buff: PropTypes.object.isRequired,
};

const isConstantBuff = b => b.uptime >= 100;
const buffKey = b => `${b.spell}-${b.name}`;

const BuffReport = ({ buffs, constantBuffs }) => {
  const mergedBuffs = [...buffs, ...constantBuffs];
  const uniqBuffs = _.uniqBy(_.filter(mergedBuffs, 'spell'), buffKey);
  const constantBuffsMerged = _.sortBy(_.filter(uniqBuffs, isConstantBuff), 'spell_name');
  const sortedBuffs = _.reverse(
    _.sortBy(_.reject(uniqBuffs, b => _.isUndefined(b.uptime) || isConstantBuff(b)), 'uptime'),
  );
  return (
    <Panel theme="primary" pt={2} pb={1} mr={0}>
      <PanelHeader p={1} mb={1}>
        Buff Uptime
      </PanelHeader>
      <Flex justify="center" mb={2}>
        <Box>
          <Text bold align="center">Constant Buffs</Text>
          <Flex gutter={2}>
            {_.map(constantBuffsMerged, buff => (
              <Box ml={2} key={buffKey(buff)}>
                <Responsive size="large">
                  {(matches) => (
                    <WowIcon
                      id={buff.spell}
                      type="spell"
                      size={matches ? 'medium' : 'small'}
                      extra={{
                        ilvl: buff.item?.ilevel,
                      }}
                      border="1px solid #ffffff33"
                    />
                  )}
                </Responsive>
              </Box>
            ))}
          </Flex>
        </Box>
      </Flex>
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <th colSpan={2}>Buff</th>
            <th colSpan={2}>Uptime</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {_.map(sortedBuffs, buff => (
            <BuffRow
              key={buffKey(buff)}
              buff={buff}
            />
          ))}
        </tbody>
      </table>
    </Panel>
  );
};

BuffReport.propTypes = {
  buffs: PropTypes.array.isRequired,
  constantBuffs: PropTypes.array,
};
BuffReport.defaultProps = {
  constantBuffs: [],
};

export default BuffReport;
